/**
 * Journey Milestone Tracking
 *
 * Tracks key milestones in the user's journey to DMARC enforcement.
 * Used to measure time-to-enforcement and identify drop-off points.
 */

import { api } from "@/lib/api";
import { canTrack } from "./session";
import type { JourneyMilestone } from "@dmarc-platform/shared";

// Track which milestones have been reported this session
const reportedThisSession = new Set<string>();

/**
 * Track a journey milestone
 *
 * @param milestone - The milestone being reached
 * @param domainId - The domain associated with this milestone (if applicable)
 * @param metadata - Additional context about the milestone
 * @returns Promise resolving to whether this was the first time reaching this milestone
 */
export async function trackMilestone(
  milestone: JourneyMilestone,
  domainId?: string,
  metadata?: Record<string, unknown>
): Promise<boolean> {
  if (!canTrack()) return false;

  // Avoid duplicate calls within the same session
  const key = `${milestone}:${domainId || ""}`;
  if (reportedThisSession.has(key)) {
    return false;
  }

  reportedThisSession.add(key);

  try {
    const response = await api.trackMilestone({
      milestone,
      domainId,
      metadata,
    });
    return response.isFirstTime;
  } catch {
    // Silently fail - analytics should never break the app
    return false;
  }
}

/**
 * Track when a user account is first created (post-OAuth signup)
 */
export function trackAccountCreated(metadata?: {
  authProvider?: string;
}): Promise<boolean> {
  return trackMilestone("account_created", undefined, metadata);
}

/**
 * Track when a user adds their first domain
 */
export function trackFirstDomainAdded(domainId: string): Promise<boolean> {
  return trackMilestone("first_domain_added", domainId);
}

/**
 * Track when a user verifies their first domain
 */
export function trackFirstDomainVerified(domainId: string): Promise<boolean> {
  return trackMilestone("first_domain_verified", domainId);
}

/**
 * Track when a user labels their first source
 */
export function trackFirstSourceLabeled(metadata?: {
  sourceId?: string;
  label?: string;
}): Promise<boolean> {
  return trackMilestone("first_source_labeled", undefined, metadata);
}

/**
 * Track when a user creates their first remediation task
 */
export function trackFirstTaskCreated(metadata?: {
  taskId?: string;
  taskType?: string;
}): Promise<boolean> {
  return trackMilestone("first_task_created", undefined, metadata);
}

/**
 * Track when a user completes their first remediation task
 */
export function trackFirstTaskCompleted(metadata?: {
  taskId?: string;
  completionMethod?: string;
}): Promise<boolean> {
  return trackMilestone("first_task_completed", undefined, metadata);
}

/**
 * Track when a domain reaches quarantine policy
 */
export function trackFirstDomainQuarantine(domainId: string): Promise<boolean> {
  return trackMilestone("first_domain_quarantine", domainId);
}

/**
 * Track when a domain reaches reject policy (enforcement)
 */
export function trackFirstDomainReject(domainId: string): Promise<boolean> {
  return trackMilestone("first_domain_reject", domainId);
}

/**
 * Track when all domains reach enforcement
 */
export function trackAllDomainsEnforced(): Promise<boolean> {
  return trackMilestone("all_domains_enforced");
}
